import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Link } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import {
  craftShard,
  getShardsCatalog,
  getShardsInventory,
  installShard,
} from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { CardElement, ColorRank } from "@/types/battle"
import { ShardTier } from "@/types/shards"
import { AFFILIATION_ELEMENTS } from "@/constants/affiliationInfo"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { ELEMENT_NAMES } from "@/constants/guardians"

export default function ShardsPage() {
  const queryClient = useQueryClient()
  const { collectionCards, syncGameState, bumpCards } = useGuardiansPassport()
  const [element, setElement] = useState<CardElement>("FIRE")
  const [tier, setTier] = useState<ShardTier>("COMMON")
  const [rank, setRank] = useState<ColorRank>("WHITE")
  const [cardId, setCardId] = useState("")
  const [shardId, setShardId] = useState("")

  const catalogQuery = useQuery({
    queryKey: ["shards-catalog"],
    queryFn: getShardsCatalog,
  })

  const invQuery = useQuery({
    queryKey: ["shards-inventory"],
    queryFn: getShardsInventory,
  })

  const craft = useMutation({
    mutationFn: () => craftShard({ element, tier, colorRank: rank }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["shards-inventory"] })
    },
  })

  const install = useMutation({
    mutationFn: () => {
      const card = collectionCards.find((c) => c.id === cardId)
      return installShard(
        { cardId, shardId, slotIndex: 0 },
        card
      )
    },
    onSuccess: async () => {
      await syncGameState()
      bumpCards()
      void invQuery.refetch()
    },
  })

  const inv = invQuery.data
  const dust = inv?.dust

  return (
    <GameScreenLayout title="Осколки" backTo="/play/collection">
      <Link
        to="/play/dust-shop"
        className="mb-3 inline-block text-xs text-guardians-accent underline"
      >
        Магазин пыли
      </Link>
      {invQuery.isLoading ? (
        <p className="text-sm text-guardians-muted">Инвентарь…</p>
      ) : inv ? (
        <section className="mb-4 text-xs text-guardians-muted">
          Пыль сегодня: {inv.battlesDustToday}/{inv.dailyBattleCap}
          <ul className="mt-2 grid grid-cols-2 gap-1">
            {AFFILIATION_ELEMENTS.map((el) => (
              <li key={el}>
                {ELEMENT_NAMES[el]}: {dust?.[el] ?? 0}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mb-6 space-y-2">
        <h3 className="text-sm font-medium">Крафт</h3>
        <div className="flex flex-wrap gap-2">
          <select
            value={element}
            onChange={(e) => setElement(e.target.value as CardElement)}
            className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs"
          >
            {AFFILIATION_ELEMENTS.map((el) => (
              <option key={el} value={el}>
                {ELEMENT_NAMES[el]}
              </option>
            ))}
          </select>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as ShardTier)}
            className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs"
          >
            <option value="COMMON">Common</option>
            <option value="RARE">Rare</option>
            <option value="EPIC">Epic</option>
          </select>
          <select
            value={rank}
            onChange={(e) => setRank(e.target.value as ColorRank)}
            className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs"
          >
            <option value="WHITE">White</option>
            <option value="GREEN">Green</option>
            <option value="BLUE">Blue</option>
          </select>
        </div>
        <button
          type="button"
          disabled={craft.isPending}
          onClick={() => craft.mutate()}
          className="rounded-xl bg-guardians-accent px-4 py-2 text-xs text-guardians-bg"
        >
          Скрафтить
        </button>
      </section>

      <section className="mb-6">
        <h3 className="mb-2 text-sm font-medium">Свободные осколки</h3>
        <ul className="space-y-1 text-sm">
          {(inv?.uninstalledShards ?? []).map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className="text-left text-guardians-accent underline"
                onClick={() => setShardId(s.id)}
              >
                {s.definition.title} ({s.tier})
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium">Установить в слот 0</h3>
        <select
          value={cardId}
          onChange={(e) => setCardId(e.target.value)}
          className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
        >
          <option value="">Карта</option>
          {collectionCards.map((c) => (
            <option key={c.id} value={c.id}>
              {c.element} Lv.{c.level}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!cardId || !shardId || install.isPending}
          onClick={() => install.mutate()}
          className="w-full rounded-xl border border-white/20 py-2 text-sm"
        >
          Установить осколок
        </button>
      </section>

      {catalogQuery.data?.shards.length ? (
        <details className="mt-6 text-xs text-guardians-muted">
          <summary>Каталог ({catalogQuery.data.shards.length})</summary>
          <ul className="mt-2 max-h-40 overflow-y-auto">
            {catalogQuery.data.shards.map((e) => (
              <li key={e.key}>
                {e.title} — {e.craftCost} пыли
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {(craft.isError || install.isError) && (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(craft.error ?? install.error, "Ошибка осколков")}
        </p>
      )}
    </GameScreenLayout>
  )
}
